/**
 * Productivity Metrics Service
 * Calculates time saved, ROI, and productivity scores based on user memory and search activity
 */

import { searchAnalytics } from './searchAnalytics';

interface ProductivityMetrics {
  timeSaved: {
    today: number;        // Minutes saved today
    thisWeek: number;     // Minutes saved this week
    thisMonth: number;    // Minutes saved this month
    total: number;        // Total minutes saved
  };
  roi: {
    hourlyValue: number;      // User's estimated hourly value
    dollarsaved: number;      // Dollar value of time saved
    monthlyROI: number;       // Monthly ROI percentage
    paybackPeriod: number;    // Days to pay back subscription
  };
  productivity: {
    score: number;            // Overall productivity score (0-100)
    searchEfficiency: number; // How efficiently user searches (0-100)
    memoryUtilization: number; // How well user utilizes memories (0-100)
    knowledgeVelocity: number; // Speed of knowledge retrieval (0-100)
  };
  insights: {
    topTimeSavers: Array<{
      activity: string;
      timeSavedMinutes: number;
      frequency: number;
      description: string;
    }>;
    productivityTrends: Array<{
      date: string;
      timeSaved: number;
      searchCount: number;
      efficiency: number;
    }>;
    recommendations: Array<{
      type: 'optimization' | 'usage' | 'upgrade';
      title: string;
      description: string;
      potentialSavings: number; // Additional minutes that could be saved
      confidence: number;
    }>;
  };
}

interface ProductivityConfig {
  // Time estimates for different activities (in minutes)
  timeEstimates: {
    manualResearch: number;        // Average time for manual research
    documentSearch: number;        // Time to search through documents
    meetingNoteReview: number;     // Time to review meeting notes
    projectContextSwitch: number;  // Time lost in context switching
    knowledgeRecreation: number;   // Time to recreate lost knowledge
  };
  // User profile settings
  userProfile: {
    hourlyValue: number;      // User's estimated hourly value in dollars
    workingHoursPerDay: number;
    workingDaysPerWeek: number;
    subscriptionCost: number; // Monthly subscription cost
  };
}

class ProductivityMetricsService {
  private static instance: ProductivityMetricsService;
  // Removed unused _STORAGE_KEY - metrics are calculated dynamically
  private readonly CONFIG_KEY = 'memara_productivity_config';
  
  private defaultConfig: ProductivityConfig = {
    timeEstimates: {
      manualResearch: 15,        // 15 minutes average for manual research
      documentSearch: 8,         // 8 minutes to search through documents
      meetingNoteReview: 12,     // 12 minutes to review meeting notes
      projectContextSwitch: 5,   // 5 minutes lost in context switching
      knowledgeRecreation: 25,   // 25 minutes to recreate lost knowledge
    },
    userProfile: {
      hourlyValue: 75,           // $75/hour default (professional rate)
      workingHoursPerDay: 8,
      workingDaysPerWeek: 5,
      subscriptionCost: 39,      // $39/month Pro plan
    }
  };

  static getInstance(): ProductivityMetricsService {
    if (!ProductivityMetricsService.instance) {
      ProductivityMetricsService.instance = new ProductivityMetricsService();
    }
    return ProductivityMetricsService.instance;
  }

  /**
   * Calculate comprehensive productivity metrics
   */
  async getProductivityMetrics(userId?: string): Promise<ProductivityMetrics> {
    const searchData = await searchAnalytics.getAnalytics(userId);
    const config = this.getConfig();
    
    // Calculate time saved based on search activity
    const timeSaved = this.calculateTimeSaved(searchData, config);
    
    // Calculate ROI metrics
    const roi = this.calculateROI(timeSaved, config);
    
    // Calculate productivity scores
    const productivity = this.calculateProductivityScores(searchData, config);
    
    // Generate insights and recommendations
    const insights = this.generateInsights(searchData, timeSaved, productivity, config);

    return {
      timeSaved,
      roi,
      productivity,
      insights
    };
  }

  /**
   * Calculate time saved based on search and memory activity
   */
  private calculateTimeSaved(searchData: any, config: ProductivityConfig) {
    const now = Date.now();
    const oneDayAgo = now - (24 * 60 * 60 * 1000);
    const oneWeekAgo = now - (7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = now - (30 * 24 * 60 * 60 * 1000);

    // Time saved per successful search (varies by query type)
    const getTimeSavedPerSearch = (queryType: 'semantic' | 'keyword' | 'exact'): number => {
      switch (queryType) {
        case 'semantic':
          return config.timeEstimates.manualResearch; // Complex research queries
        case 'exact':
          return config.timeEstimates.documentSearch; // Specific document searches
        case 'keyword':
          return config.timeEstimates.meetingNoteReview; // General keyword searches
        default:
          return config.timeEstimates.documentSearch;
      }
    };

    // Calculate time saved for different periods
    const calculatePeriodSavings = (periodStart: number) => {
      const periodSearches = searchData.weeklyTrend
        .filter((day: any) => new Date(day.date).getTime() >= periodStart)
        .reduce((sum: number, day: any) => sum + day.count, 0);

      // Estimate distribution of query types
      const semanticRatio = 0.4; // 40% semantic searches (high value)
      const exactRatio = 0.3;    // 30% exact searches (medium value)
      const keywordRatio = 0.3;  // 30% keyword searches (lower value)

      const semanticSavings = periodSearches * semanticRatio * getTimeSavedPerSearch('semantic');
      const exactSavings = periodSearches * exactRatio * getTimeSavedPerSearch('exact');
      const keywordSavings = periodSearches * keywordRatio * getTimeSavedPerSearch('keyword');

      return Math.round(semanticSavings + exactSavings + keywordSavings);
    };

    // Additional time saved from context switching reduction
    const contextSwitchSavings = searchData.searchesThisWeek * 
      (config.timeEstimates.projectContextSwitch * 0.5); // 50% reduction in context switching

    const today = calculatePeriodSavings(oneDayAgo);
    const thisWeek = calculatePeriodSavings(oneWeekAgo) + contextSwitchSavings;
    const thisMonth = calculatePeriodSavings(oneMonthAgo) + (contextSwitchSavings * 4);
    const total = thisMonth * 3; // Estimate total based on 3 months of usage

    return {
      today,
      thisWeek,
      thisMonth,
      total: Math.round(total)
    };
  }

  /**
   * Calculate ROI metrics
   */
  private calculateROI(timeSaved: any, config: ProductivityConfig) {
    const hourlyValue = config.userProfile.hourlyValue;
    const monthlySubscription = config.userProfile.subscriptionCost;
    
    // Convert time saved to dollar value
    const dollarsaved = Math.round((timeSaved.thisMonth / 60) * hourlyValue);
    
    // Calculate monthly ROI percentage
    const monthlyROI = monthlySubscription > 0 
      ? Math.round(((dollarsaved - monthlySubscription) / monthlySubscription) * 100)
      : 0;
    
    // Calculate payback period in days
    const dailySavings = dollarsaved / 30; // Average daily savings
    const paybackPeriod = dailySavings > 0 
      ? Math.ceil(monthlySubscription / dailySavings)
      : 999;

    return {
      hourlyValue,
      dollarsaved,
      monthlyROI,
      paybackPeriod
    };
  }

  /**
   * Calculate productivity scores
   */
  private calculateProductivityScores(searchData: any, _config: ProductivityConfig) {
    // Check if user has any activity - if not, return zeros
    const hasActivity = searchData.totalSearches > 0 || searchData.searchesThisWeek > 0;
    
    if (!hasActivity) {
      return {
        score: 0,
        searchEfficiency: 0,
        memoryUtilization: 0,
        knowledgeVelocity: 0
      };
    }

    // Search Efficiency: Based on success rate and response time
    const searchEfficiency = Math.min(100, 
      (searchData.successRate * 0.7) + 
      (Math.max(0, 100 - (searchData.averageResponseTime / 10)) * 0.3)
    );

    // Memory Utilization: Based on search frequency vs total memories
    const searchFrequency = searchData.searchesThisWeek / 7; // Daily average
    const memoryUtilization = searchData.totalSearches > 0 ? Math.min(100, 
      (searchFrequency / Math.max(1, searchData.totalSearches / 30)) * 100
    ) : 0;

    // Knowledge Velocity: How quickly user finds information
    const avgResponseTime = searchData.averageResponseTime;
    const knowledgeVelocity = searchData.totalSearches > 0 ? 
      Math.max(0, 100 - (avgResponseTime / 20)) : 0; // Only calculate if there are searches

    // Overall Productivity Score (weighted average)
    const score = Math.round(
      (searchEfficiency * 0.4) + 
      (memoryUtilization * 0.3) + 
      (knowledgeVelocity * 0.3)
    );

    return {
      score,
      searchEfficiency: Math.round(searchEfficiency),
      memoryUtilization: Math.round(memoryUtilization),
      knowledgeVelocity: Math.round(knowledgeVelocity)
    };
  }

  /**
   * Generate insights and recommendations
   */
  private generateInsights(searchData: any, timeSaved: any, productivity: any, config: ProductivityConfig) {
    const topTimeSavers = [
      {
        activity: 'Semantic Memory Search',
        timeSavedMinutes: Math.round(searchData.searchPatterns.queryTypes.semantic * config.timeEstimates.manualResearch),
        frequency: searchData.searchPatterns.queryTypes.semantic,
        description: 'Complex research queries answered instantly'
      },
      {
        activity: 'Document Retrieval',
        timeSavedMinutes: Math.round(searchData.searchPatterns.queryTypes.exact * config.timeEstimates.documentSearch),
        frequency: searchData.searchPatterns.queryTypes.exact,
        description: 'Specific documents found without manual searching'
      },
      {
        activity: 'Context Switching Reduction',
        timeSavedMinutes: Math.round(searchData.searchesThisWeek * config.timeEstimates.projectContextSwitch * 0.5),
        frequency: searchData.searchesThisWeek,
        description: 'Less time lost switching between information sources'
      }
    ].sort((a, b) => b.timeSavedMinutes - a.timeSavedMinutes);

    // Generate productivity trends (last 7 days)
    const productivityTrends = searchData.weeklyTrend.map((day: any) => ({
      date: day.date,
      timeSaved: Math.round(day.count * config.timeEstimates.documentSearch),
      searchCount: day.count,
      efficiency: Math.min(100, (day.avgResponseTime > 0 ? 100 - (day.avgResponseTime / 20) : 100))
    }));

    // Generate recommendations only if user has some activity
    const recommendations = [];
    const hasActivity = searchData.totalSearches > 0 || searchData.searchesThisWeek > 0;
    
    if (hasActivity) {
      if (productivity.searchEfficiency < 70) {
        recommendations.push({
          type: 'optimization' as const,
          title: 'Improve Search Query Quality',
          description: 'Use more specific keywords to improve search success rate',
          potentialSavings: 5,
          confidence: 85
        });
      }

      if (searchData.searchesThisWeek < 10) {
        recommendations.push({
          type: 'usage' as const,
          title: 'Increase Memory Usage',
          description: 'Search your memories more frequently to maximize time savings',
          potentialSavings: 30,
          confidence: 90
        });
      }
    }

    if (timeSaved.thisMonth > 120 && productivity.score > 75) {
      recommendations.push({
        type: 'upgrade' as const,
        title: 'Consider Pro Features',
        description: 'Advanced search and automation could save even more time',
        potentialSavings: 60,
        confidence: 80
      });
    }

    return {
      topTimeSavers,
      productivityTrends,
      recommendations
    };
  }

  /**
   * Get user configuration
   */
  private getConfig(): ProductivityConfig {
    try {
      const stored = localStorage.getItem(this.CONFIG_KEY);
      if (stored) {
        return { ...this.defaultConfig, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.warn('Failed to load productivity config:', error);
    }
    return this.defaultConfig;
  }

  /**
   * Update user configuration
   */
  updateConfig(updates: Partial<ProductivityConfig>): void {
    try {
      const currentConfig = this.getConfig();
      const newConfig = { ...currentConfig, ...updates };
      localStorage.setItem(this.CONFIG_KEY, JSON.stringify(newConfig));
      console.log('✅ Productivity config updated');
    } catch (error) {
      console.error('Failed to update productivity config:', error);
    }
  }

  /**
   * Get quick productivity summary for dashboard
   */
  async getQuickSummary(userId?: string): Promise<{
    timeSavedThisWeek: number;
    dollarsaved: number;
    productivityScore: number;
    topInsight: string;
  }> {
    const metrics = await this.getProductivityMetrics(userId);
    
    const topInsight = metrics.insights.topTimeSavers.length > 0
      ? `${metrics.insights.topTimeSavers[0].activity} saved ${metrics.insights.topTimeSavers[0].timeSavedMinutes} min`
      : 'Start searching to see productivity insights';

    return {
      timeSavedThisWeek: metrics.timeSaved.thisWeek,
      dollarsaved: metrics.roi.dollarsaved,
      productivityScore: metrics.productivity.score,
      topInsight
    };
  }

  /**
   * Clear productivity data and reset metrics
   * @deprecated Demo data generation removed - use real productivity tracking
   */
  clearDemoData(): void {
    // Reset all productivity metrics to clean state
    console.log('🗑️ Productivity metrics cleared - using real data only');
  }
}

// Export singleton instance
export const productivityMetrics = ProductivityMetricsService.getInstance();

// Export types
export type { ProductivityMetrics, ProductivityConfig }; 